/* Pikemla — inline quick-add on product cards.
 *
 * One-tap add for single-variant products; inline size chips for the rest
 * (Fashion Nova style) — no modal, no page navigation.
 *
 * After a successful add it fires the theme's own `cart:update` event, which
 * makes cart-items-component re-render the drawer and makes pk-ship-progress
 * repaint the free-shipping bar; then it opens the drawer so the shopper
 * sees their free-shipping progress.
 *
 * If the add fails (size sold out mid-session) the chips are refreshed from
 * /products/<handle>.js so availability is never stale for long.
 */
(function () {
  'use strict';

  var SELECTOR = '[data-pk-quick-add]';

  function qs(sel, root) {
    return (root || document).querySelector(sel);
  }

  function qsa(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function setOpen(container, open) {
    var panel = qs('[data-pk-qa-panel]', container);
    var toggle = qs('[data-pk-qa-toggle]', container);
    if (!panel || !toggle) return;
    container.classList.toggle('is-open', open);
    panel.hidden = !open;
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  function closeOthers(except) {
    qsa(SELECTOR + '.is-open').forEach(function (el) {
      if (el !== except) setOpen(el, false);
    });
  }

  function notifyCartUpdated() {
    // The theme's own event: cart-items-component re-renders the drawer,
    // pk-ship-progress refreshes the free-shipping bar.
    document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: {} }));
  }

  function openCartDrawer() {
    var drawer = qs('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function') {
      try {
        drawer.open();
      } catch (e) {
        /* drawer unavailable (e.g. cart page) — cart still updated */
      }
    }
  }

  function showNote(container, msg) {
    var note = qs('[data-pk-qa-note]', container);
    if (!note) return;
    note.textContent = msg;
    note.hidden = false;
    clearTimeout(note._t);
    note._t = setTimeout(function () {
      note.hidden = true;
    }, 4000);
  }

  /* Re-read live availability after a failed add (sold out mid-session). */
  function refreshChips(container) {
    var handle = container.getAttribute('data-handle');
    var sizeIdx = parseInt(container.getAttribute('data-size-idx') || '0', 10);
    if (!handle) return Promise.resolve();
    return fetch('/products/' + handle + '.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (p) {
        if (!p || !p.variants) return;
        qsa('[data-pk-qa-chip]', container).forEach(function (chip) {
          var value = chip.getAttribute('data-value');
          var firstId = 0;
          var minQty = Infinity;
          var anyAvail = false;
          p.variants.forEach(function (v) {
            if (v['option' + (sizeIdx + 1)] !== value) return;
            if (!v.available) return;
            anyAvail = true;
            if (!firstId) firstId = v.id;
            if (typeof v.inventory_quantity === 'number' && v.inventory_quantity < minQty) {
              minQty = v.inventory_quantity;
            }
          });
          var bolt = chip.querySelector('.pk-quick-add__bolt');
          if (anyAvail) {
            chip.disabled = false;
            chip.removeAttribute('aria-disabled');
            chip.setAttribute('data-variant-id', String(firstId));
            var low = minQty <= 5;
            chip.classList.toggle('is-low', low);
            if (low) {
              chip.setAttribute('data-qty', String(minQty));
              chip.setAttribute('title', 'Only ' + minQty + ' left');
              if (!bolt) {
                var s = document.createElement('span');
                s.className = 'pk-quick-add__bolt';
                s.setAttribute('aria-hidden', 'true');
                s.textContent = '\u26a1';
                chip.insertBefore(s, chip.firstChild);
              }
            } else {
              chip.removeAttribute('data-qty');
              chip.removeAttribute('title');
              if (bolt) bolt.remove();
            }
          } else {
            chip.disabled = true;
            chip.setAttribute('aria-disabled', 'true');
            chip.setAttribute('title', 'Sold out');
            chip.classList.remove('is-low');
            if (bolt) bolt.remove();
          }
        });
      })
      .catch(function () {
        /* keep the server-rendered state on network failure */
      });
  }

  function addVariant(variantId, btn, container) {
    if (btn.disabled) return;
    var isToggle = btn.hasAttribute('data-pk-qa-toggle');
    var textSpan = isToggle ? qs('[data-pk-qa-toggle-text]', btn) : null;
    var originalText = textSpan ? textSpan.textContent : null;

    btn.disabled = true;
    btn.classList.add('is-loading');
    if (textSpan) textSpan.textContent = 'Adding\u2026';

    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 }),
    })
      .then(function (r) {
        if (!r.ok) throw new Error('add failed: ' + r.status);
        return r.json();
      })
      .then(function () {
        if (textSpan) textSpan.textContent = 'Added \u2713';
        btn.classList.add('is-added');
        notifyCartUpdated();
        // Let the drawer re-render get a head start, then reveal it with
        // the free-shipping progress front and center.
        setTimeout(openCartDrawer, 200);
        setTimeout(function () {
          btn.classList.remove('is-added', 'is-loading');
          btn.disabled = false;
          if (textSpan) textSpan.textContent = originalText;
          setOpen(container, false);
        }, 1400);
      })
      .catch(function () {
        btn.classList.remove('is-loading');
        btn.disabled = false;
        if (textSpan) textSpan.textContent = originalText;
        refreshChips(container).then(function () {
          showNote(container, 'That size just sold out \u2014 sizes updated.');
        });
      });
  }

  document.addEventListener('click', function (e) {
    var toggle = e.target.closest('[data-pk-qa-toggle]');
    if (toggle) {
      e.preventDefault();
      var container = toggle.closest(SELECTOR);
      if (!container || toggle.disabled) return;
      var singleId = toggle.getAttribute('data-pk-qa-variant');
      if (singleId) {
        addVariant(parseInt(singleId, 10), toggle, container);
        return;
      }
      var willOpen = !container.classList.contains('is-open');
      closeOthers(container);
      setOpen(container, willOpen);
      return;
    }

    var chip = e.target.closest('[data-pk-qa-chip]');
    if (chip) {
      e.preventDefault();
      if (chip.disabled) return;
      var c2 = chip.closest(SELECTOR);
      var vid = parseInt(chip.getAttribute('data-variant-id'), 10);
      if (c2 && vid) addVariant(vid, chip, c2);
      return;
    }

    if (!e.target.closest(SELECTOR)) closeOthers(null);
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeOthers(null);
  });

  /* ---- Card overlays: float the velocity badge + quick-add icon onto the
   * product photo so neither takes layout space in the card (no gaps, no
   * uneven rows). Runs on load and for cards added later (search,
   * recommendations, infinite scroll). ---- */
  function placeCardOverlays(scope) {
    var list = [];
    if (scope && scope.tagName === 'PRODUCT-CARD') list.push(scope);
    if (scope && scope.querySelectorAll) {
      Array.prototype.forEach.call(scope.querySelectorAll('product-card'), function (c) {
        list.push(c);
      });
    }
    list.forEach(function (card) {
      if (card.hasAttribute('data-pk-overlays')) return;
      card.setAttribute('data-pk-overlays', '1');
      var media = card.querySelector('.product-media');
      if (!media) return;
      var badge = card.querySelector('.pk-velocity');
      var qa = card.querySelector(SELECTOR);
      if (badge) media.appendChild(badge);
      if (qa) media.appendChild(qa);
    });
  }

  function initCardOverlays() {
    placeCardOverlays(document);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCardOverlays);
  } else {
    initCardOverlays();
  }
  if ('MutationObserver' in window && document.body) {
    new MutationObserver(function (muts) {
      muts.forEach(function (m) {
        Array.prototype.forEach.call(m.addedNodes, function (n) {
          if (n && n.nodeType === 1) placeCardOverlays(n);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }
})();
